import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import {
  Printer, PenTool, Car, UtensilsCrossed, Package, UserCheck, Lock,
  ChevronRight, Loader2, Clock, CheckCircle2, XCircle, AlertCircle, Send
} from 'lucide-react';

const CATEGORY_CONFIG = {
  stationery: { label: 'Stationery & Print Design', icon: PenTool, color: 'text-violet-500', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
  printing: { label: 'Printing & Scanning', icon: Printer, color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  parking: { label: 'Parking', icon: Car, color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  food_beverage: { label: 'Food & Beverage', icon: UtensilsCrossed, color: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  courier: { label: 'Courier & Dispatch', icon: Package, color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  visitor: { label: 'Visitor Management', icon: UserCheck, color: 'text-cyan-500', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
  storage: { label: 'Storage & Lockers', icon: Lock, color: 'text-rose-500', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-amber-500/20 text-amber-500 border-amber-500/30' },
  in_progress: { label: 'In Progress', icon: AlertCircle, color: 'bg-blue-500/20 text-blue-500 border-blue-500/30' },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'bg-red-500/20 text-red-500 border-red-500/30' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-500/20 text-red-500 border-red-500/30' },
};

export const ServicesTab = ({ api, venues, theme }) => {
  const { cardBg, cardBorder, innerCardBg } = theme;
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [requestModal, setRequestModal] = useState(null); // service object
  const [requestForm, setRequestForm] = useState({ centre_id: '', quantity: 1, notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState('browse'); // browse | my_requests

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [servRes, reqRes] = await Promise.all([
        api.get('/services/catalog'),
        api.get('/services/requests'),
      ]);
      setServices(servRes.data);
      setRequests(reqRes.data);
    } catch (err) {
      console.error('Failed to load services:', err);
    } finally {
      setLoading(false);
    }
  };

  const activeVenues = venues.filter(v => v.status === 'active');

  const groupedServices = services.filter(s => s.is_active).reduce((acc, s) => {
    if (!acc[s.category]) acc[s.category] = [];
    acc[s.category].push(s);
    return acc;
  }, {});

  const handleSubmitRequest = async () => {
    if (!requestForm.centre_id) {
      toast.error('Please select a center');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/services/requests', {
        service_id: requestModal.id,
        centre_id: requestForm.centre_id,
        quantity: requestForm.quantity,
        notes: requestForm.notes,
      });
      toast.success(`Request submitted! Estimated: ₹${res.data.estimated_amount}`);
      setRequestModal(null);
      setRequestForm({ centre_id: '', quantity: 1, notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="services-tab">
      {/* Header Toggle */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setView('browse')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            view === 'browse' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="browse-services-btn"
        >
          Browse Services
        </button>
        <button
          onClick={() => setView('my_requests')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            view === 'my_requests' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="my-requests-btn"
        >
          My Requests
          {requests.filter(r => r.status === 'pending').length > 0 && (
            <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">
              {requests.filter(r => r.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      {view === 'browse' ? (
        /* ========= BROWSE SERVICES ========= */
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Object.entries(CATEGORY_CONFIG).map(([catKey, catConfig]) => {
            const catServices = groupedServices[catKey] || [];
            if (catServices.length === 0) return null;
            const CatIcon = catConfig.icon;
            const isExpanded = expandedCategory === catKey;

            return (
              <Card
                key={catKey}
                className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-all ${
                  isExpanded ? 'sm:col-span-2 lg:col-span-3 xl:col-span-4' : ''
                }`}
                data-testid={`category-${catKey}`}
              >
                <CardContent className="p-0">
                  <button
                    onClick={() => setExpandedCategory(isExpanded ? null : catKey)}
                    className="w-full p-4 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl ${catConfig.bg} flex items-center justify-center`}>
                        <CatIcon className={`w-5 h-5 ${catConfig.color}`} />
                      </div>
                      <div>
                        <p className="font-heading font-semibold text-sm">{catConfig.label}</p>
                        <p className="text-xs text-muted-foreground">{catServices.length} services</p>
                      </div>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>

                  {isExpanded && (
                    <div className={`border-t ${cardBorder} p-4`}>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {catServices.map(service => (
                          <div
                            key={service.id}
                            className={`p-4 rounded-xl border ${cardBorder} ${innerCardBg} flex flex-col justify-between`}
                            data-testid={`service-${service.id}`}
                          >
                            <div>
                              <p className="font-medium text-sm">{service.name}</p>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{service.description}</p>
                            </div>
                            <div className="flex items-center justify-between mt-3">
                              <div>
                                {service.base_price > 0 ? (
                                  <p className="text-sm font-semibold text-primary">
                                    ₹{service.base_price} <span className="text-xs font-normal text-muted-foreground">{service.unit}</span>
                                  </p>
                                ) : (
                                  <Badge variant="outline" className="text-xs text-emerald-500 border-emerald-500/30">Free</Badge>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-xs border-primary/30 text-primary hover:bg-primary/10"
                                onClick={() => { setRequestModal(service); setRequestForm({ centre_id: '', quantity: 1, notes: '' }); }}
                                data-testid={`request-btn-${service.id}`}
                              >
                                <Send className="w-3 h-3 mr-1" /> Request
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* ========= MY REQUESTS ========= */
        <div className="space-y-3">
          {requests.length === 0 ? (
            <Card className={`${cardBg} ${cardBorder}`}>
              <CardContent className="p-12 text-center">
                <div className="w-14 h-14 rounded-2xl bg-muted/10 flex items-center justify-center mx-auto mb-4">
                  <Package className="w-7 h-7 text-muted-foreground" />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-1">No service requests yet</h3>
                <p className="text-sm text-muted-foreground mb-4">Browse services and submit your first request</p>
                <Button variant="outline" size="sm" onClick={() => setView('browse')}>Browse Services</Button>
              </CardContent>
            </Card>
          ) : (
            requests.map(req => {
              const sc = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
              const catConfig = CATEGORY_CONFIG[req.service_category] || CATEGORY_CONFIG.stationery;
              const CatIcon = catConfig.icon;
              return (
                <Card key={req.id} className={`${cardBg} ${cardBorder}`} data-testid={`request-${req.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-lg ${catConfig.bg} flex items-center justify-center shrink-0`}>
                          <CatIcon className={`w-4 h-4 ${catConfig.color}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{req.service_name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {req.centre_name} &middot; Qty: {req.quantity}
                            {req.notes && ` &middot; ${req.notes}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {req.estimated_amount > 0 && (
                          <p className="text-sm font-semibold">₹{req.estimated_amount}</p>
                        )}
                        <Badge className={`${sc.color} border text-xs`}>{sc.label}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Request Modal */}
      <Dialog open={!!requestModal} onOpenChange={(open) => { if (!open) setRequestModal(null); }}>
        <DialogContent className={cardBg}>
          <DialogHeader>
            <DialogTitle className="font-heading">Request: {requestModal?.name}</DialogTitle>
          </DialogHeader>
          {requestModal && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">{requestModal.description}</p>
              {requestModal.base_price > 0 && (
                <p className="text-sm">Price: <span className="font-semibold text-primary">₹{requestModal.base_price}</span> {requestModal.unit}</p>
              )}

              <div className="space-y-2">
                <Label>Center *</Label>
                <Select value={requestForm.centre_id} onValueChange={(v) => setRequestForm(f => ({...f, centre_id: v}))}>
                  <SelectTrigger data-testid="select-centre">
                    <SelectValue placeholder="Select center" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeVenues.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min={1}
                  value={requestForm.quantity}
                  onChange={(e) => setRequestForm(f => ({...f, quantity: parseInt(e.target.value) || 1}))}
                  data-testid="quantity-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input
                  placeholder="Any specific requirements..."
                  value={requestForm.notes}
                  onChange={(e) => setRequestForm(f => ({...f, notes: e.target.value}))}
                  data-testid="notes-input"
                />
              </div>

              {requestModal.base_price > 0 && (
                <div className={`p-3 rounded-lg ${innerCardBg} border ${cardBorder}`}>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Estimated Total</span>
                    <span className="font-semibold text-primary">₹{requestModal.base_price * requestForm.quantity}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestModal(null)}>Cancel</Button>
            <Button
              onClick={handleSubmitRequest}
              disabled={submitting || !requestForm.centre_id}
              className="bg-primary hover:bg-primary/90"
              data-testid="submit-request-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
